import { FC, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Style } from '@capacitor/status-bar';

import { SuccessStep } from '@mopla/constants';
import {
	Footer,
	MainWithFooterTemplate,
	useStatusBarStyleSync,
} from '@mopla/ui';
import moplaWelcomeLogo from '@mopla/ui/icons/moplaWelcomeLogo.svg?url';
import { ContentWrapper } from '@mopla/ui/layouts/WelcomeScreen';
import { useLinearFlowSteps } from '@mopla/utils';

import {
	ERegistrationFormSteps,
	RegistrationForm,
} from '../../../../organisms/RegistrationForm';
import { WelcomeStep } from '../../constants';
import { PrivacyAndTermsRow } from '../../PrivacyAndTermsRow';
import { useWelcomePageStoreContext } from '../../useWelcomePageStore';
import { TSetInterceptor } from '../../utils/useInterceptor';
import { MoplaLogoImgStyled } from '../styled';

export interface IRegistrationFlowProps {
	setBackInterceptor: TSetInterceptor;
	setCloseInterceptor: TSetInterceptor;
	onBottomSliderClose: VoidFunction;
}

export const RegistrationFlow: FC<IRegistrationFlowProps> = ({
	setBackInterceptor,
	setCloseInterceptor,
	onBottomSliderClose,
}) => {
	const { activeStep, successType, setSuccessType } =
		useWelcomePageStoreContext();
	const navigate = useNavigate();

	const showVerifyEmailSent = successType === SuccessStep.verifyEmail;

	const {
		setPrevStep,
		activeStep: activeRegistrationStep,
		goToStep,
	} = useLinearFlowSteps(
		ERegistrationFormSteps,
		ERegistrationFormSteps.SelectFlow
	);

	const getRegistrationBack = useCallback(() => {
		const isPrevSet = setPrevStep();

		if (!isPrevSet) {
			navigate({ pathname: '/welcome' });
		}

		return false;
	}, [setPrevStep, navigate]);

	const { push, pop } = useStatusBarStyleSync();

	useEffect(() => {
		push({
			title: 'RegistrationFlow',
			style: Style.Light,
		});

		return () => {
			pop();
		};
	}, [activeStep, push, pop]);

	useEffect(() => {
		if (activeStep === WelcomeStep.registration && !showVerifyEmailSent) {
			setBackInterceptor(getRegistrationBack);

			return () => {
				setBackInterceptor(null);
			};
		} else {
			setBackInterceptor(null);
		}

		return;
	}, [
		activeStep,
		getRegistrationBack,
		setBackInterceptor,
		showVerifyEmailSent,
	]);

	useEffect(() => {
		if (showVerifyEmailSent) {
			setCloseInterceptor(onBottomSliderClose);

			return () => {
				setCloseInterceptor(null);
			};
		}

		return;
	}, [showVerifyEmailSent, setCloseInterceptor, onBottomSliderClose]);

	return (
		<RegistrationForm
			activeRegStep={activeRegistrationStep}
			setActiveRegStep={goToStep}
			onSuccess={setSuccessType}
			showVerifyEmailSent={showVerifyEmailSent}
			render={({ content, submit }) => (
				<MainWithFooterTemplate
					className="mainWithFooter"
					content={
						<ContentWrapper>
							{!successType && (
								<MoplaLogoImgStyled
									className="logo"
									src={moplaWelcomeLogo}
									alt=""
								/>
							)}
							{content}
						</ContentWrapper>
					}
					footer={
						<Footer>
							{activeRegistrationStep === ERegistrationFormSteps.SelectFlow &&
								!successType && <PrivacyAndTermsRow />}
							{submit}
						</Footer>
					}
				/>
			)}
		/>
	);
};
